import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { usePost } from '../API';
import { Account, AccountBalanceReminder, AccountBalanceReminderConfig, EditAccountBalanceReminderParams } from '@aidkitorg/aidkit/lib/admin_panel/accounts';
import * as Sentry from "@sentry/react";
import { logAdminPanelError } from '../utils/adminPanel.utils';

type AccountsContextType = {
  totalBalance: number | undefined,
  accounts: Array<Account>
  subledgers: string[]
  accountsDataTimestamp?: number

  accountBalanceReminders: Array<AccountBalanceReminder>,

  isAccountsLoading: boolean,
  accountBalanceReminderCallInFlight: boolean,

  fetchAccountsOverview: (force?: boolean) => void,
  fetchAccountBalanceReminders: () => void,
  createAccountBalanceReminder: ( params: AccountBalanceReminderConfig ) => Promise<boolean>,
  editAccountBalanceReminder: ( params: EditAccountBalanceReminderParams ) => void
}

type AccountsOverviewRef = {
  accounts?: Array<Account>,
  timestamp?: number,
  expiresAt?: number,
}

type AccountsProviderProps = {
  children: React.ReactNode
}

const AccountsContext = createContext<AccountsContextType>({
  totalBalance: undefined,
  accounts: [],
  subledgers: [],

  accountBalanceReminders: [],

  isAccountsLoading: false,
  accountBalanceReminderCallInFlight: false,

  fetchAccountsOverview: async (_force?: boolean) => {},
  fetchAccountBalanceReminders: async () => {},
  createAccountBalanceReminder: async (_params?: AccountBalanceReminderConfig) => { return true },
  editAccountBalanceReminder: async (_params?: EditAccountBalanceReminderParams) => {},
})

export const useAccounts = () => {
  const context = useContext(AccountsContext)
  if (!context) {
    throw new Error("useAccounts must be used within an AccountsProvider")
  }

  return context;
}

export const AccountsProvider: React.FC<AccountsProviderProps> = ({ children }) => {
  const [totalBalance, setTotalBalance] = useState<number | undefined>()
  const [accounts, setAccounts] = useState<Array<Account>>([])
  const [subledgers, setSubledgers] = useState<string[]>([])
  const [accountsDataTimestamp, setAccountsDataTimestamp] = useState<number>()

  const [accountBalanceReminders, setAccountBalanceReminders] = useState<Array<AccountBalanceReminder>>([])

  const [isAccountsLoading, setIsAccountsLoading] = useState(false)
  const [accountBalanceReminderCallInFlight, setAccountBalanceReminderCallInFlight] = useState(false)

  const accountsRef = useRef<AccountsOverviewRef>({})

  const getAccountsData = usePost("/api/admin_panel/accounts")

  const getAccountBalanceReminders = usePost("/api/admin_panel/accounts/balance_reminders")
  const createAccountBalanceReminderPost = usePost("/api/admin_panel/accounts/balance_reminders/create")
  const editAccountBalanceReminderPost = usePost("/api/admin_panel/accounts/balance_reminders/edit")
  
  const fetchAccountsOverview = useCallback(async (force?: boolean) => {
    const now = Date.now()

    if (!force &&
      accountsRef.current.accounts && 
      (accountsRef.current.expiresAt && now < accountsRef.current.expiresAt)
    ) {
      return
    }

    setIsAccountsLoading(true)
    const result = await getAccountsData({ force })
    setIsAccountsLoading(false)

    if (result && result.success && result.data) {
      accountsRef.current = {
        accounts: result.data.accounts,
        timestamp: result.data.timestamp,
        expiresAt: result.data.expiresAt
      }

      setTotalBalance(result.data.totalBalance)
      setAccounts(result.data.accounts)
      setSubledgers(result.data.subledgers || [])
      setAccountsDataTimestamp(result.data.timestamp)
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }, [getAccountsData])

  const fetchAccountBalanceReminders = useCallback(async () => {
    setAccountBalanceReminderCallInFlight(true)
    const result = await getAccountBalanceReminders({})
    setAccountBalanceReminderCallInFlight(false)
    
    if (result && result.success && result.data) {
      setAccountBalanceReminders(result.data.reminders)
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }, [ getAccountBalanceReminders ])

  const createAccountBalanceReminder = useCallback(async ( params: AccountBalanceReminderConfig ) => {
    setAccountBalanceReminderCallInFlight(true)
    const result = await createAccountBalanceReminderPost(params)

    if (result && !result.success && result.error) {
      logAdminPanelError(result.error, result.message)
    }
    await fetchAccountBalanceReminders()
    
    setAccountBalanceReminderCallInFlight(false)

    return result.success
  }, [ createAccountBalanceReminderPost ])

  const editAccountBalanceReminder = useCallback(async (params: EditAccountBalanceReminderParams) => {
    setAccountBalanceReminderCallInFlight(true)

    const result = await editAccountBalanceReminderPost(params)

    if (result && !result.success && result.error) {
      logAdminPanelError(result.error, result.message)
    } else {
      await fetchAccountBalanceReminders()
    }

    setAccountBalanceReminderCallInFlight(false)
  }, [ editAccountBalanceReminderPost])

  const value = {
    totalBalance: totalBalance,
    accounts: accounts,
    subledgers: subledgers,
    accountsDataTimestamp: accountsDataTimestamp,

    accountBalanceReminders: accountBalanceReminders,

    isAccountsLoading: isAccountsLoading,
    accountBalanceReminderCallInFlight: accountBalanceReminderCallInFlight,

    fetchAccountsOverview: fetchAccountsOverview,
    fetchAccountBalanceReminders: fetchAccountBalanceReminders,
    createAccountBalanceReminder: createAccountBalanceReminder,
    editAccountBalanceReminder: editAccountBalanceReminder,
  }

  return (
    <AccountsContext.Provider value={value}>
      {children}
    </AccountsContext.Provider>
  )
}